import { useCallback, useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';

import type { ClinicianOnboardingOrchestrator } from '../model/useClinicianOnboardingOrchestrator';
import type { PrescriberDetailsPayload } from '../model/useClinicianOnboardingOrchestrator';
import { progressPhaseFor } from '../model/progress';

import { OnboardingProgress } from './OnboardingProgress';
import { ConsumingInviteStep } from './steps/ConsumingInviteStep';
import { FillEmployeeRecordStep } from './steps/FillEmployeeRecordStep';
import { IdpDemographicInfoStep } from './steps/IdpDemographicInfoStep';
import { IdpDisclaimerStep } from './steps/IdpDisclaimerStep';
import { IdpLegalAgreementsStep } from './steps/IdpLegalAgreementsStep';
import { IdpOtpStep } from './steps/IdpOtpStep';
import { IdpQuestionsStep } from './steps/IdpQuestionsStep';
import { InviteFailedStep } from './steps/InviteFailedStep';
import { LinkBankAccountStep } from './steps/LinkBankAccountStep';
import { ApplicationRejectedStep } from './steps/ApplicationRejectedStep';
import { PendingTimelineStep } from './steps/PendingTimelineStep';
import { CredentialingStep } from './steps/CredentialingStep';
import { InitialScreeningStep } from './steps/InitialScreeningStep';
import { OnboardingResultStep } from './steps/OnboardingResultStep';
import { PrescriberDetailsStep } from './steps/PrescriberDetailsStep';
import { WizardStepSkeleton } from './steps/_shared/WizardStepSkeleton';
import { useSubmitOnceOnMount } from './steps/_shared/useSubmitOnceOnMount';
import { DS_STEP_FORM_ID } from './steps/_shared/dsStepFormId';

export interface ClinicianOnboardingWizardProps {
  orchestrator: ClinicianOnboardingOrchestrator;
}

const AWAITING_TIMEOUT_MS = 90_000;

type StepKind = ClinicianOnboardingOrchestrator['step']['kind'];

const INTERACTIVE_STEPS = new Set<StepKind>([
  'submit_application',
  'fill_credentialing_form',
  'fill_employee_record',
  'enter_prescriber_details',
  'sign_disclaimer',
  'sign_legal_agreements',
  'submit_demographics',
  'answer_questions',
  'link_bank_account',
]);

function primaryLabelFor(kind: StepKind): string {
  switch (kind) {
    case 'submit_application':
      return 'Submit application';
    case 'fill_credentialing_form':
      return 'Submit credentialing';
    case 'sign_disclaimer':
    case 'sign_legal_agreements':
      return 'Accept and continue';
    case 'enter_prescriber_details':
      return 'Looks good';
    default:
      return 'Continue';
  }
}

interface StepHeading {
  title: string;
  subtitle?: string;
}

function headingFor(step: ClinicianOnboardingOrchestrator['step']): StepHeading | null {
  switch (step.kind) {
    case 'fill_employee_record':
      return { title: "You're in! Please confirm your profile" };
    case 'submit_application':
      return {
        title: 'Apply as a physician',
      };
    case 'application_pending':
      return {
        title: 'We are reviewing your application',
        subtitle: 'We will notify you once a decision has been made.',
      };
    case 'application_rejected':
      return {
        title: 'Application not accepted',
        subtitle:
          'Unfortunately your application was not accepted at this time. If you believe this is an error, please contact support.',
      };
    case 'fill_credentialing_form':
      return {
        title: 'Complete credentialing',
      };
    case 'credentialing_pending':
      return {
        title: 'Credentialing under review',
        subtitle:
          'Your credentialing information has been submitted and is being reviewed. We will notify you once a decision has been made.',
      };
    case 'enter_prescriber_details':
      return {
        title: 'Confirm your prescriber details',
        subtitle:
          'Please confirm that the details below match the registered name attached to your NPI. If anything looks off, click Edit to make changes.',
      };
    case 'sign_disclaimer':
      return { title: 'Review disclaimers' };
    case 'sign_legal_agreements':
      return { title: 'Review legal agreement' };
    case 'submit_demographics':
      return {
        title: 'We need to verify your identity',
        subtitle:
          'Enter your personal home address, legal name, and phone number exactly as they appear on your credit card statement. This information is sent securely to Experian and is not stored.',
      };
    case 'answer_questions':
      return step.data.questions.length > 0
        ? { title: 'Answer verification questions' }
        : {
            title: 'We texted you a verification link',
            subtitle:
              "Please verify your identity with Experian, our verification vendor. This page will automatically refresh once you're verified",
          };
    case 'submit_otp':
      return {
        title: 'We texted you a verification link',
        subtitle:
          "Please verify your identity with Experian, our verification vendor. This page will automatically refresh once you're verified.",
      };
    case 'link_bank_account':
      return {
        title: 'Link your bank account',
        subtitle: 'To finish onboarding, please add a bank account for payouts.',
      };
    default:
      return null;
  }
}

const GATED_STEPS = new Set<StepKind>([
  'sign_disclaimer',
  'sign_legal_agreements',
  'link_bank_account',
]);

export function ClinicianOnboardingWizard({ orchestrator }: ClinicianOnboardingWizardProps) {
  const { step, isSubmitting } = orchestrator;
  const [gate, setGate] = useState<{ kind: StepKind; cleared: boolean }>({
    kind: step.kind,
    cleared: false,
  });
  if (gate.kind !== step.kind) {
    setGate({ kind: step.kind, cleared: false });
  }
  const isGated = GATED_STEPS.has(step.kind);
  const stepReady = isGated ? gate.cleared : true;
  const handleReadyChange = useCallback(
    (ready: boolean) =>
      setGate((prev) => (prev.cleared === ready ? prev : { ...prev, cleared: ready })),
    []
  );

  const phase = progressPhaseFor(step);
  const showProgress =
    phase !== 'invite' &&
    phase !== 'application' &&
    phase !== 'credentialing' &&
    phase !== 'complete';
  const showButtons =
    INTERACTIVE_STEPS.has(step.kind) &&
    !(step.kind === 'answer_questions' && step.data.questions.length === 0);
  const heading = headingFor(step);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col px-10 pb-10">
      <div className="shrink-0">
        {showProgress ? <OnboardingProgress currentPhase={phase} className="mb-6" /> : null}
        {heading ? (
          <div className="mb-4">
            <h1 className="text-2xl font-medium">{heading.title}</h1>
            {heading.subtitle ? (
              <p className="text-muted-foreground mt-1 text-xs">{heading.subtitle}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <StepRenderer
          key={step.kind}
          orchestrator={orchestrator}
          onReadyChange={handleReadyChange}
        />
      </div>

      {showButtons ? (
        <div className="shrink-0 pt-6">
          <div className="relative">
            <Button
              type="submit"
              form={DS_STEP_FORM_ID}
              radius="pill"
              size="lg"
              className="w-full disabled:opacity-30"
              disabled={isSubmitting || !stepReady}
            >
              <span className={isSubmitting ? 'opacity-0' : undefined}>
                {primaryLabelFor(step.kind)}
              </span>
            </Button>
            {isSubmitting ? (
              <LoaderCircle className="pointer-events-none absolute inset-0 m-auto size-5 animate-spin" />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface StepRendererProps {
  orchestrator: ClinicianOnboardingOrchestrator;
  onReadyChange: (ready: boolean) => void;
}

function StepRenderer({ orchestrator, onReadyChange }: StepRendererProps) {
  const { step, submit, refresh, isSubmitting, submitError, resetError } = orchestrator;

  switch (step.kind) {
    case 'loading':
      return (
        <div className="flex h-full w-full items-center justify-center">
          <WizardStepSkeleton />
        </div>
      );

    case 'fetch_error':
      return (
        <Alert variant="destructive">
          <AlertTitle>Could not load account status</AlertTitle>
          <AlertDescription>
            <div className="flex flex-col gap-2">
              <div>{step.message}</div>
              <div>
                <Button type="button" variant="secondary" onClick={refresh}>
                  Try again
                </Button>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      );

    case 'consuming_invite':
      return (
        <ConsumingInviteStep
          submit={submit}
          isSubmitting={isSubmitting}
          submitError={submitError}
        />
      );

    case 'invite_failed':
      return <InviteFailedStep message={step.message} onRetry={refresh} />;

    case 'fill_employee_record':
      return (
        <FillEmployeeRecordStep
          prefill={step.data.currentValues}
          onSubmit={(payload) => submit(payload)}
          isSubmitting={isSubmitting}
          submitError={submitError}
          onResetError={resetError}
        />
      );

    case 'submit_application':
      return (
        <InitialScreeningStep
          onSubmit={(payload) => submit(payload)}
          isSubmitting={isSubmitting}
          submitError={submitError}
        />
      );

    case 'application_pending':
      return <PendingTimelineStep label="Application" />;

    case 'application_rejected':
      return <ApplicationRejectedStep />;

    case 'fill_credentialing_form':
      return (
        <CredentialingStep
          onSubmit={(payload) => submit(payload)}
          isSubmitting={isSubmitting}
          submitError={submitError}
        />
      );

    case 'credentialing_pending':
      return <PendingTimelineStep label="Credentialing" />;

    case 'enter_prescriber_details':
      return (
        <PrescriberDetailsStep
          prefill={step.data.prefill}
          registrationError={step.data.registrationError}
          onSubmit={(payload: PrescriberDetailsPayload) => submit(payload)}
          submitError={submitError}
        />
      );

    case 'start_idp':
      return (
        <AutoStartIdpStep submit={submit} isSubmitting={isSubmitting} submitError={submitError} />
      );

    case 'sign_disclaimer':
      return (
        <IdpDisclaimerStep
          onSubmit={() => submit()}
          isSubmitting={isSubmitting}
          submitError={submitError}
          onReadyChange={onReadyChange}
        />
      );

    case 'sign_legal_agreements':
      return (
        <IdpLegalAgreementsStep
          pendingAgreementIds={step.data.pendingAgreementIds}
          onSubmit={(nameSigned) => submit({ nameSigned })}
          isSubmitting={isSubmitting}
          submitError={submitError}
          onReadyChange={onReadyChange}
        />
      );

    case 'submit_demographics':
      return (
        <IdpDemographicInfoStep
          onSubmit={(demographicInfo) => submit({ demographicInfo })}
          submitError={submitError}
        />
      );

    case 'answer_questions':
      return (
        <IdpQuestionsStep
          questions={step.data.questions}
          answerChoices={step.data.answerChoices}
          onSubmit={(answers) => submit({ answers })}
          onRefresh={refresh}
          isSubmitting={isSubmitting}
          submitError={submitError}
        />
      );

    case 'submit_otp':
      return <IdpOtpStep onRefresh={refresh} />;

    case 'awaiting_dosespot':
      return <AwaitingDosespotStep onRefresh={refresh} />;

    case 'link_bank_account':
      return (
        <LinkBankAccountStep
          onSubmit={(payload) => submit(payload)}
          isSubmitting={isSubmitting}
          submitError={submitError}
          onReadyChange={onReadyChange}
        />
      );

    case 'complete':
      return <OnboardingResultStep kind="success" />;

    default: {
      const _exhaustive: never = step;
      void _exhaustive;
      return null;
    }
  }
}

interface AutoStartIdpStepProps {
  submit: (payload?: unknown) => void;
  isSubmitting: boolean;
  submitError: Error | null;
}

interface AwaitingDosespotStepProps {
  onRefresh: () => void;
}

function AwaitingDosespotStep({ onRefresh }: AwaitingDosespotStepProps) {
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsOverdue(true), AWAITING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  if (isOverdue) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <WizardStepSkeleton />
        <h2 className="text-lg font-medium">This is taking longer than expected</h2>
        <p className="text-muted-foreground max-w-xs text-sm">
          DoseSpot has not responded yet. You can keep waiting or try refreshing.
        </p>
        <Button type="button" radius="pill" variant="secondary" onClick={onRefresh}>
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center">
      <WizardStepSkeleton />
    </div>
  );
}

function AutoStartIdpStep({ submit, submitError }: AutoStartIdpStepProps) {
  useSubmitOnceOnMount(submit);

  if (submitError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <WizardStepSkeleton />
        <h2 className="text-lg font-medium">Could not start identity proofing</h2>
        <p className="text-muted-foreground max-w-xs text-sm">{submitError.message}</p>
        <Button type="button" radius="pill" variant="secondary" onClick={() => submit()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center">
      <WizardStepSkeleton />
    </div>
  );
}
